import { useState, useCallback, useEffect, useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useDocumentTitle } from "usehooks-ts";
import debounce from "lodash.debounce";
import {
  Loader2,
  ListChecks,
  Settings,
  Folder,
  ChevronRight,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  NoteListTableView,
  SchemaEditorDialog,
  ListSettingsDialog,
  DeleteListDialog,
  FolderView,
} from "@/components/note-lists";
import { WorkflowConfigPopover } from "@/components/workflow";
import { noteListQueryOptions } from "@/queries/noteLists";
import {
  useUpdateNoteList,
  useUpdateNoteInList,
  useRemoveNoteFromList,
  useReorderNoteInList,
} from "@/mutations/noteLists";
import { useNoteLists } from "@/hooks/useNoteLists";
import { getAncestorChain } from "@/lib/note-list-utils";
import { MarkdownEditor } from "@/components/markdown-editor";
import { cn } from "@/utils";

export const Route = createFileRoute("/lists/$listId")({
  component: ListDetailPage,
});

function ListDetailPage() {
  const { listId } = Route.useParams();
  const { user } = Route.useRouteContext();

  const [schemaEditorOpen, setSchemaEditorOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState<string | null>(null);

  const {
    data: list,
    isLoading,
    error,
  } = useQuery(noteListQueryOptions(listId));

  const { data: allLists = [] } = useNoteLists();

  const updateListMutation = useUpdateNoteList();
  const updateNoteMutation = useUpdateNoteInList();
  const removeNoteMutation = useRemoveNoteFromList();
  const reorderNoteMutation = useReorderNoteInList();

  const debouncedSave = useMemo(
    () =>
      debounce((value: string) => {
        updateListMutation.mutate({
          id: listId,
          data: { description: value || null },
        });
      }, 500),
    [listId],
  );

  useEffect(() => {
    return () => debouncedSave.cancel();
  }, [debouncedSave]);

  const handleDescriptionChange = useCallback(
    (markdown: string) => {
      setDescriptionValue(markdown);
      debouncedSave(markdown);
    },
    [debouncedSave],
  );

  useEffect(() => {
    if (updateListMutation.isSuccess) {
      const timer = setTimeout(() => updateListMutation.reset(), 2000);
      return () => clearTimeout(timer);
    }
  }, [updateListMutation.isSuccess]);

  const handleUpdateProperties = useCallback(
    (noteId: string, properties: Record<string, unknown>) => {
      updateNoteMutation.mutate({
        listId,
        noteId,
        properties,
      });
    },
    [listId, updateNoteMutation],
  );

  const handleRemoveNote = useCallback(
    (noteId: string) => {
      removeNoteMutation.mutate({ listId, noteId });
    },
    [listId, removeNoteMutation],
  );

  const handleRemoveNotes = useCallback(
    (noteIds: string[]) => {
      noteIds.forEach((noteId) => {
        removeNoteMutation.mutate({ listId, noteId });
      });
    },
    [listId, removeNoteMutation],
  );

  const handleReorderNote = useCallback(
    (noteId: string, newSortOrder: number) => {
      reorderNoteMutation.mutate({ listId, noteId, sortOrder: newSortOrder });
    },
    [listId, reorderNoteMutation],
  );

  const handleColumnReorder = useCallback(
    (orderedKeys: string[]) => {
      if (!list) return;
      const currentViewConfig = list.viewConfig || {
        defaultView: "table" as const,
      };
      updateListMutation.mutate({
        id: listId,
        data: {
          viewConfig: {
            ...currentViewConfig,
            columnOrder: orderedKeys,
          },
        },
      });
    },
    [listId, list, updateListMutation],
  );

  useDocumentTitle(list ? `${list.name} | Lists` : "Loading... | Lists");

  const breadcrumbs = list ? getAncestorChain(listId, allLists) : [];

  if (!user) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center space-y-4">
          <ListChecks className="h-16 w-16 text-muted-foreground mx-auto" />
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-foreground">List</h2>
            <p className="text-muted-foreground">Sign in to view this list</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !list) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center space-y-4">
          <ListChecks className="h-16 w-16 text-muted-foreground mx-auto" />
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-foreground">
              Not Found
            </h2>
            <p className="text-muted-foreground">
              {error instanceof Error
                ? error.message
                : "This list doesn't exist or you don't have access to it."}
            </p>
          </div>
          <Button asChild>
            <Link to="/lists">Back to Lists</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isFolder = list.kind === "FOLDER";

  const sortedNotes = [...(list.notes || [])].sort((a, b) => {
    if (a.sortOrder !== null && b.sortOrder !== null) {
      return a.sortOrder - b.sortOrder;
    }
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col gap-2 p-4 md:p-6 pb-4 border-b border-border/40">
        {breadcrumbs.length > 0 && (
          <nav
            className="flex items-center gap-1 text-sm text-muted-foreground"
            aria-label="Breadcrumb"
          >
            <Link
              to="/lists"
              className="hover:text-foreground transition-colors"
            >
              Lists
            </Link>
            {breadcrumbs.map((crumb) => (
              <span key={crumb.id} className="flex items-center gap-1">
                <ChevronRight className="h-3 w-3" />
                <Link
                  to="/lists/$listId"
                  params={{ listId: crumb.id }}
                  className="hover:text-foreground transition-colors"
                >
                  {crumb.name}
                </Link>
              </span>
            ))}
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground font-medium">{list.name}</span>
          </nav>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isFolder && <Folder className="h-6 w-6 text-amber-500" />}
            <h1 className="text-2xl font-semibold">{list.name}</h1>
            {(updateListMutation.isPending || updateListMutation.isSuccess) && (
              <span
                className={cn(
                  "text-xs transition-opacity",
                  updateListMutation.isPending
                    ? "text-muted-foreground"
                    : "text-green-600 dark:text-green-500",
                )}
              >
                {updateListMutation.isPending ? "Saving..." : "Saved"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {!isFolder && (
              <WorkflowConfigPopover
                listId={listId}
                workflowEnabled={list.workflowEnabled ?? false}
                workflowPrompt={list.workflowPrompt ?? null}
                workflowConfig={list.workflowConfig ?? null}
              />
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDeleteOpen(true)}
              title="Delete list"
              aria-label="Delete list"
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSettingsOpen(true)}
              title="List settings"
              aria-label="Open list settings"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <MarkdownEditor
          content={descriptionValue ?? list?.description ?? ""}
          onChange={handleDescriptionChange}
          placeholder="Add a description..."
          className="text-muted-foreground text-sm"
          editable={true}
        />
      </div>

      <div className="flex-1 p-2 md:p-6 overflow-y-auto overflow-x-hidden min-w-0">
        {isFolder ? (
          <FolderView folderId={listId} folderName={list.name} />
        ) : (
          <NoteListTableView
            listId={listId}
            listName={list.name}
            notes={sortedNotes}
            schema={list.schema}
            viewConfig={list.viewConfig}
            smartFillEnabled={list.smartFillEnabled}
            workflowEnabled={list.workflowEnabled}
            onRemoveNote={handleRemoveNote}
            onRemoveNotes={handleRemoveNotes}
            onUpdateProperties={handleUpdateProperties}
            onReorderNote={handleReorderNote}
            onEditSchema={() => setSchemaEditorOpen(true)}
            onColumnReorder={handleColumnReorder}
          />
        )}
      </div>

      {!isFolder && (
        <SchemaEditorDialog
          open={schemaEditorOpen}
          onOpenChange={setSchemaEditorOpen}
          listId={listId}
          listName={list.name}
          schema={list.schema}
        />
      )}

      <ListSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        list={list}
      />

      <DeleteListDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        list={list}
        childCount={list._count?.children ?? 0}
      />
    </div>
  );
}
